import {
    ChangeDetectionStrategy,
    Component,
    Input,
    model,
    OnDestroy,
    output,
    ViewEncapsulation,
} from '@angular/core';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { ObjectTypes } from '@fi-shared/types';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { Subject } from 'rxjs';
import { Animations } from '@fi-shared/utils/animations';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';

@Component({
    selector: 'fi-name-description-input',
    templateUrl: './name-description-input.component.html',
    styleUrls: ['./name-description-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-name-description-input' },
    animations: [Animations.fade],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiNameDescriptionInputComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiNameDescriptionInputComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class FiNameDescriptionInputComponent
    extends ControlValueAccessorValidatorBase<string | IContentLanguageLabelModel[]>
    implements OnDestroy
{
    @Input() modelName: string = 'Name';
    @Input() descriptions: string | IContentLanguageLabelModel[] = [];
    @Input() selectedLanguageId: number;
    @Input({ required: true }) namePlaceholderTextId: string;
    @Input() namePlaceholderHintTextId: string;
    @Input() descriptionPlaceholderTextId: string = 'common.description';
    @Input() descriptionPlaceholderHintTextId: string;
    @Input({ required: true }) showAttachmentsArea: boolean;
    @Input({ required: true }) objectType: ObjectTypes;
    @Input() nameFormFieldClassName: string;
    @Input() parentDialogClassName: string;
    @Input() nameRequiredErrorTextId: string = 'common_error.mandatory';
    @Input() isDisabled: boolean = false;
    @Input() isInsertLinkEnabled: boolean = true;
    @Input() disabledNameTooltipText: string;

    public mediaBankFileReferences = model<IMediaBankFileReference[]>([]);

    public descriptionsChange = output<string | IContentLanguageLabelModel[]>();

    private readonly stringDescriptionRef = { Value: '' };

    protected isPreferredContentLangLabelModel(labelModel: IContentLanguageLabelModel): boolean {
        return labelModel.ContentLanguageId === this.selectedLanguageId;
    }

    protected isNameRequired(): boolean {
        return !Array.isArray(this.ngModel)
            ? !this.ngModel?.length
            : !this.ngModel?.find((name: IContentLanguageLabelModel) => name.Value);
    }

    protected isStringDescriptionModel(): boolean {
        return !Array.isArray(this.descriptions);
    }

    protected getLabelDescriptions(): IContentLanguageLabelModel[] {
        return Array.isArray(this.descriptions) ? this.descriptions : [];
    }

    protected getStringDescriptionRef(): { Value: string } {
        const value = typeof this.descriptions === 'string' ? this.descriptions : '';
        if (this.stringDescriptionRef.Value !== value) {
            this.stringDescriptionRef.Value = value;
        }
        return this.stringDescriptionRef;
    }

    protected onDescriptionChange(description: { Value: string }): void {
        if (!Array.isArray(this.descriptions)) {
            this.descriptions = description.Value;
        }
        this.descriptionsChange.emit(this.descriptions);
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        if (this.isNameRequired()) {
            return {
                nameRequired: true,
            };
        }
        return null;
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly ObjectTypes = ObjectTypes;
}
